import type { FieldValidation } from '@sentra/contracts';
import { countryName, DEFAULT_PHONE_COUNTRY, joinPhone, splitPhone } from '@sentra/contracts';
import type * as React from 'react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/design/components/input';
import { DialCodeCombobox } from '@/features/persons/form/dial-code-combobox';
import { useLocale } from '@/i18n';

/**
 * A telephone field: the country's dialling prefix, chosen, beside the number itself.
 *
 * **The country was always part of the answer and the form used to hide the question.** This was a
 * single box that normalized to E.164 on blur, so an operator holding a Moroccan passport had to
 * know to type `+212`, and the national form `0612345678` typed into a field whose default region
 * is Spain was read as Spanish, found not to be a Spanish number, and refused. The prefix picker
 * asks for the country; `joinPhone` turns the country and the digits into the one stored form, and
 * strips the trunk prefix a plan defines rather than concatenating around it.
 *
 * **E.164 is still the stored form and nothing about that changed.** It is what the projection
 * indexes, so `+34 612 34 56 78`, `0034612345678` and `612345678` all have to reach one record.
 * What is new is that this control can no longer produce anything else.
 *
 * **It is FULLY DERIVED from `value`, and the only local state is the prefix.** Mirroring a prop
 * into state is unavailable here in both of its usual forms — this package promotes every
 * `react-hooks` rule to an error, so neither a set during render nor a set inside an effect passes
 * — and keying the control on `value` to force a remount would take focus away on every keystroke.
 * Deriving is the better design regardless: an amendment opening, a conflict resolution taking the
 * other copy, and the completeness dialog all reach the screen for free, and there is no second
 * copy of the number that can go stale.
 *
 * **The prefix is the exception because a blank field has to remember it.** With the number box
 * empty the composed value is the empty string — that invariant is what keeps a blank optional
 * field OMITTED from the request rather than sent as `''` — so `value` carries no calling code and
 * there is nowhere else for the operator's choice to live until they type a digit. Emptying the box
 * writes the prefix into that state for the same reason: the country they were working under does
 * not stop being true because they are retyping the number.
 *
 * **The number box holds digits.** Anything else is stripped as it is typed, and no lone `+` can sit
 * in a box whose content is derived from a value with no room for one. A whole international number
 * is a PASTE, which arrives complete and moves the prefix to match — the one case a prefix picker
 * must not break. Editing inside the number is ordinary: React rewrites the input only when the
 * derived text differs from what the DOM already holds, so inserting a digit mid-string leaves the
 * caret where it was. A REJECTED character does send the caret to the end, which is the price of
 * refusing it at all, and the trunk zero disappearing when a number completes does the same once.
 *
 * **Blur canonicalises, because a value can arrive in a shape this control would not produce.** An
 * API client may write `0034612345678`, which the server stores verbatim; the split SHOWS it as
 * `+34` and `612345678`, and without this an amendment that never touched the field would send back
 * the shape it arrived in while the screen showed another. The single box this replaces normalised
 * on blur for the same reason, and blur is the last moment before a Save that is an event rather
 * than a render.
 *
 * **A shared calling code names the region the number belongs to, not the one that was picked.**
 * `+1 212…` is the United States and `+1 416…` is Canada, so picking one and typing the other's
 * number shows the other once the number is complete. The plan is shared; saying otherwise would be
 * the screen inventing a fact.
 */
export function PhoneInput({
  id,
  value,
  validation,
  onChange,
  invalid,
  describedBy,
}: Readonly<{
  id: string;
  value: string;
  validation: Extract<FieldValidation, { type: 'phone' }>;
  onChange: (next: string) => void;
  invalid: boolean;
  describedBy?: string;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const fallbackRegion = validation.defaultCountry ?? DEFAULT_PHONE_COUNTRY;

  const [chosen, setChosen] = useState<Prefix | null>(null);
  const parts = useMemo(() => splitPhone(value, fallbackRegion), [value, fallbackRegion]);

  // The value is the authority whenever it has one; `chosen` only fills the gap a blank one leaves.
  // A number that will not yet parse can still say its calling code, and then only its REGION is
  // unknown — which is what the second expression falls back for.
  const prefix: Prefix =
    value === ''
      ? (chosen ?? { region: parts.region, callingCode: parts.callingCode })
      : { region: parts.region ?? chosen?.region ?? null, callingCode: parts.callingCode };

  // The country to confirm underneath, or `null` for nothing to confirm. Held as the REGION rather
  // than as a boolean so the render below narrows it rather than re-deriving the same condition.
  const detectedId = `${id}-detected`;
  const detectedRegion = parts.national === '' ? null : prefix.region;
  const describes = [describedBy, detectedRegion === null ? undefined : detectedId]
    .filter((part) => part !== undefined)
    .join(' ');

  function emit(next: string): void {
    if (next !== value) {
      onChange(next);
    }
  }

  function onNumberChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const national = event.target.value.replace(/\D/gu, '');
    const next = joinPhone({ ...prefix, national });
    if (next === '') {
      // Emptying the box must not drop the prefix back to the field's default. The operator chose a
      // country, or the record arrived with one, and they are about to type a number under it.
      setChosen(prefix);
    }
    emit(next);
  }

  /**
   * A whole international number, pasted.
   *
   * Handled here rather than in the change handler because a paste arrives complete: a `+` typed
   * one character at a time cannot survive in a box whose content is derived from an E.164 value,
   * while a pasted `+380441234567` is unambiguous and is what somebody copying a number out of a
   * document actually does. Anything else falls through to the ordinary digits-only path.
   */
  function onNumberPaste(event: React.ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData('text').trim();
    if (!pasted.startsWith('+') && !pasted.startsWith('00')) {
      return;
    }
    event.preventDefault();
    const international = splitPhone(pasted.replace(/^00/u, '+'), fallbackRegion);
    setChosen({ region: international.region, callingCode: international.callingCode });
    emit(joinPhone(international));
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-2">
        <DialCodeCombobox
          value={prefix.region}
          callingCode={prefix.callingCode}
          onChange={(option) => {
            const picked = { region: option.value, callingCode: option.callingCode };
            setChosen(picked);
            emit(joinPhone({ ...picked, national: parts.national }));
          }}
        />
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          className="flex-1"
          aria-invalid={invalid}
          {...(describes === '' ? {} : { 'aria-describedby': describes })}
          value={parts.national}
          onChange={onNumberChange}
          onPaste={onNumberPaste}
          onBlur={() => {
            // `emit` is a no-op when the composed value already equals the stored one, so this
            // fires only for a value some other writer put there in a shape this control does not
            // produce. A half-typed number composes to exactly what it already is, so leaving the
            // field mid-entry changes nothing.
            emit(joinPhone({ ...prefix, national: parts.national }));
          }}
        />
      </div>
      {detectedRegion !== null && (
        <p id={detectedId} aria-live="polite" className="text-xs text-muted-foreground">
          {t('person.form.phoneDetected', { country: countryName(detectedRegion, locale) })}
        </p>
      )}
    </div>
  );
}

/** The half of a number the picker owns: which country, and the code that follows from it. */
interface Prefix {
  readonly region: string | null;
  readonly callingCode: string;
}
